import type { SxProps, Theme } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { InHouseLineChart } from "../../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { COST_VARIANCE } from "../../../theme/tokens";
import type { OtMode } from "../../../utils/models";
import { formatCurrency } from "../../../utils/utilFunctions";
import { useGetCostVariance } from "../CostDeepDive/hooks";
import { buildClampRange, costToKDollars, fetchedDataToChartDataGroup } from "../utils";

type Props = {
	trailing?: ReactNode;
	containerSx?: SxProps<Theme>;
	sinceDate: DateTime;
	endDate?: DateTime;
	appliedRoleFilter?: string[];
	appliedJobFilter?: string[];
	laborCostEnabled?: boolean;
	shiftHoursForApi?: string[];
	excludeHolidays?: boolean;
	includeLateScheduled?: boolean;
	shrinkLegendForSmallScreens?: boolean;
	otMode?: OtMode;
	estimateMissingWage: boolean;
	departmentId?: string;
	/** The page's `mcSettingsReady`: persisted settings and the Shift Hours scope are both reconciled. */
	ready: boolean;
};

export const ScheduledVsActualCost: FC<Props> = ({
	trailing,
	containerSx,
	endDate,
	sinceDate,
	shiftHoursForApi,
	appliedRoleFilter,
	appliedJobFilter,
	laborCostEnabled,
	excludeHolidays,
	includeLateScheduled,
	shrinkLegendForSmallScreens,
	otMode,
	estimateMissingWage,
	departmentId,
	ready,
}) => {
	const { timezone } = useHospitalData();
	const clampRange = buildClampRange(sinceDate, endDate, timezone);
	const { data: costVarianceData, isLoading: isCostVarianceLoadingQuery } = useGetCostVariance({
		sinceDate,
		endDate,
		roles: appliedRoleFilter,
		jobs: appliedJobFilter,
		laborCost: laborCostEnabled,
		shiftHours: shiftHoursForApi,
		excludeHolidays,
		includeLateScheduled,
		otMode,
		estimateMissingWage,
		departmentId,
		// `estimateMissingWage` (and laborCost / includeLateScheduled / otMode above) are persisted
		// `mc_*` settings, and roles/jobs/shiftHours wait on the Shift Hours scope — wait for both rather
		// than fetching twice and charting the guess first.
		enabled: ready,
	});
	// A gated-off query reports `isLoading: false`, so fold the wait in to keep the chart's skeleton up.
	const isCostVarianceLoading = isCostVarianceLoadingQuery || !ready;

	const lines =
		!isCostVarianceLoading && costVarianceData
			? [
					fetchedDataToChartDataGroup({
						data: costVarianceData.actualOnlyCost,
						name: "Unplanned",
						timezone,
						clampRange,
						color: COST_VARIANCE.UNPLANNED,
					}),
					fetchedDataToChartDataGroup({
						data: costVarianceData.scheduledOnlyCost,
						name: "No punch",
						timezone,
						clampRange,
						color: COST_VARIANCE.NO_PUNCH,
					}),
					fetchedDataToChartDataGroup({
						data: costVarianceData.overlapDiffCost,
						name: "Schedule mismatch",
						timezone,
						clampRange,
						color: COST_VARIANCE.MISMATCH,
					}),
				]
			: undefined;

	const tooltipSortOrder = {
		Unplanned: 0,
		"No punch": 1,
		"Schedule mismatch": 2,
	};

	const legendDescriptions = {
		Unplanned: "Cost for shifts that were worked but not scheduled.",
		"No punch": "Cost for shifts that were scheduled but not worked.",
		"Schedule mismatch": "Difference in cost for shifts that were both scheduled and worked.",
	};

	const tooltipLabelFormatter = (d: DateTime) => d.toFormat("EEEE, MMMM d");

	return (
		<CommonDashboardBox
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "start",
				gap: 1,
				width: "calc(33% - 4px)",
				...containerSx,
			}}
		>
			<CommonDashboardHeader label="Scheduled vs Actual Cost" trailing={trailing} showBadge="beta" />
			<InHouseLineChart
				shrinkLegendForSmallScreens={shrinkLegendForSmallScreens}
				margin={{
					top: 4,
					right: 16,
					bottom: 0,
					left: 20,
				}}
				width="100%"
				height={containerSx ? "100%" : undefined}
				tooltipSortOrder={tooltipSortOrder}
				tooltipValueFormatter={formatCurrency}
				tooltipLabelFormatter={tooltipLabelFormatter}
				legendDescriptions={legendDescriptions}
				leftAxisData={
					lines
						? {
								dataGroups: lines,
								tickRenderer: (value) => costToKDollars(value) ?? "$0.0K",
							}
						: undefined
				}
			/>
		</CommonDashboardBox>
	);
};
